import * as THREE from 'three'
import type { Quality } from '../types'
import { causticTexture } from '../procedural/materials'
import { worldWidth, worldDepth } from '../utils/grid'
import type { LevelTheme } from '../data/level-themes'
import { updateReefLight } from '../game/world/reef-materials'
import { anchorSkyboxToCamera } from './SkyDome'

/** Underwater atmosphere: gradient sky dome, fog, lighting, god-rays, caustics.
 * `applyTheme` retints fog / sky / lights so each level reads as a distinct location. */
export class Environment {
  private godRays: THREE.Mesh[] = []
  private caustic: THREE.Mesh
  private causticTex: THREE.CanvasTexture
  readonly keyLight: THREE.DirectionalLight
  private skyMat: THREE.ShaderMaterial
  private skybox: THREE.Mesh
  private skyboxCamera: THREE.Camera | null
  private hemi: THREE.HemisphereLight
  private rim: THREE.DirectionalLight
  private ambient: THREE.AmbientLight
  private fog: THREE.Fog
  private scene: THREE.Scene
  private t = 0

  constructor(scene: THREE.Scene, quality: Quality, camera?: THREE.Camera) {
    this.scene = scene
    this.skyboxCamera = camera ?? null
    // Fog for depth — deep ocean tint.
    this.fog = new THREE.Fog(0x07344a, 14, 100)
    scene.fog = this.fog
    scene.background = new THREE.Color(0x0a2f44)

    // Gradient sky dome.
    const skyGeo = new THREE.SphereGeometry(180, 32, 16)
    const skyMat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      uniforms: {
        top: { value: new THREE.Color(0x1a8f9e) },
        mid: { value: new THREE.Color(0x0e5f78) },
        bottom: { value: new THREE.Color(0x05202f) },
      },
      vertexShader: /* glsl */ `
        varying vec3 vPos;
        void main() { vPos = position; gl_Position = projectionMatrix * modelViewMatrix * vec4(position,1.0); }
      `,
      fragmentShader: /* glsl */ `
        varying vec3 vPos;
        uniform vec3 top; uniform vec3 mid; uniform vec3 bottom;
        void main() {
          float h = normalize(vPos).y * 0.5 + 0.5;
          vec3 c = h < 0.5 ? mix(bottom, mid, h*2.0) : mix(mid, top, (h-0.5)*2.0);
          gl_FragColor = vec4(c, 1.0);
        }
      `,
    })
    this.skyMat = skyMat
    this.skybox = new THREE.Mesh(skyGeo, skyMat)
    scene.add(this.skybox)
    if (camera) anchorSkyboxToCamera(this.skybox, camera)

    // Lighting rig.
    this.hemi = new THREE.HemisphereLight(0xb8e2df, 0x223b42, 0.25)
    scene.add(this.hemi)

    this.keyLight = new THREE.DirectionalLight(0xffedca, 2.4)
    this.keyLight.position.set(-22, 35, -16)
    this.keyLight.castShadow = quality !== 'low'
    const shadowSize = quality === 'high' ? 2048 : 1024
    this.keyLight.shadow.mapSize.set(shadowSize, shadowSize)
    const span = Math.max(worldWidth(), worldDepth()) * 0.62
    const sc = this.keyLight.shadow.camera as THREE.OrthographicCamera
    sc.left = -span
    sc.right = span
    sc.top = span
    sc.bottom = -span
    sc.near = 1
    sc.far = 120
    this.keyLight.shadow.bias = -0.00015
    this.keyLight.shadow.normalBias = 0.012
    scene.add(this.keyLight)
    scene.add(this.keyLight.target)

    this.rim = new THREE.DirectionalLight(0xafd2d8, 0.28)
    this.rim.position.set(20, 16, 22)
    scene.add(this.rim)

    this.ambient = new THREE.AmbientLight(0x1f6973, 0.08)
    scene.add(this.ambient)

    // God-ray light shafts.
    const rayTex = this.makeRayTexture()
    const rayCount = quality === 'low' ? 3 : quality === 'medium' ? 5 : 7
    const rayMat = new THREE.MeshBasicMaterial({
      map: rayTex,
      transparent: true,
      opacity: 0.035,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
      side: THREE.DoubleSide,
    })
    for (let i = 0; i < rayCount; i++) {
      const ray = new THREE.Mesh(new THREE.PlaneGeometry(4, 44), rayMat)
      ray.position.set((i / rayCount - 0.5) * worldWidth() * 1.1, 14, (Math.random() - 0.5) * worldDepth())
      ray.rotation.set(0.2, Math.random() * Math.PI, 0.18)
      this.godRays.push(ray)
      scene.add(ray)
    }

    // Animated caustic overlay on the seabed.
    this.causticTex = causticTexture(256)
    this.causticTex.repeat.set(6, 6)
    this.caustic = new THREE.Mesh(
      new THREE.PlaneGeometry(worldWidth() + 6, worldDepth() + 6),
      new THREE.MeshBasicMaterial({
        map: this.causticTex,
        transparent: true,
        opacity: 0.055,
        blending: THREE.AdditiveBlending,
        depthWrite: false,
      }),
    )
    this.caustic.rotation.x = -Math.PI / 2
    this.caustic.position.y = -0.74
    scene.add(this.caustic)
  }

  /** Retint fog / sky-dome / lighting to a level theme (no geometry rebuild). */
  applyTheme(theme: LevelTheme): void {
    this.fog.color.setHex(theme.fog.color).lerp(new THREE.Color(0x126580), 0.4)
    this.fog.near = 22
    this.fog.far = Math.max(65, 100 * 0.018 / theme.fog.density)
    if (this.scene.background instanceof THREE.Color) this.scene.background.setHex(theme.fog.color)
    this.skyMat.uniforms.top.value.setHex(theme.sky.top)
    this.skyMat.uniforms.mid.value.setHex(theme.sky.mid)
    this.skyMat.uniforms.bottom.value.setHex(theme.sky.bottom)
    this.keyLight.color.setHex(theme.light.key).lerp(new THREE.Color(0xe9f5ef), 0.88)
    this.keyLight.intensity = theme.light.keyIntensity * 1.48
    this.hemi.color.setHex(theme.light.hemiSky).lerp(new THREE.Color(0xa6dce7), 0.55)
    this.hemi.groundColor.setHex(theme.light.hemiGround).lerp(new THREE.Color(0x216c89), 0.4)
    this.ambient.color.setHex(theme.light.ambient)
  }

  /** Re-anchor the title skybox after the attract camera has moved. */
  syncSkybox(camera?: THREE.Camera): void {
    if (camera) this.skyboxCamera = camera
    if (this.skyboxCamera) anchorSkyboxToCamera(this.skybox, this.skyboxCamera)
  }

  private makeRayTexture(): THREE.CanvasTexture {
    const cv = document.createElement('canvas')
    cv.width = 64
    cv.height = 256
    const ctx = cv.getContext('2d')!
    const g = ctx.createLinearGradient(0, 0, 64, 0)
    g.addColorStop(0, 'rgba(220,245,255,0)')
    g.addColorStop(0.5, 'rgba(220,245,255,0.7)')
    g.addColorStop(1, 'rgba(220,245,255,0)')
    ctx.fillStyle = g
    ctx.fillRect(0, 0, 64, 256)
    return new THREE.CanvasTexture(cv)
  }

  update(dt: number): void {
    updateReefLight(dt)
    this.syncSkybox()
    this.t += dt
    for (let i = 0; i < this.godRays.length; i++) {
      this.godRays[i].rotation.z = 0.18 + Math.sin(this.t * 0.3 + i) * 0.08
    }
    this.causticTex.offset.x = this.t * 0.012
    this.causticTex.offset.y = this.t * 0.008
  }
}
